import type { FieldValue, FieldValueExtended } from "@scayla/api-client-fetch";
import type { ColoredIcon } from "@scayla/components";
import { fileTypeIcon } from "@scayla/components";

export function recordFileTypeIcon(
  fieldValues: Record<string, FieldValue | FieldValueExtended> | undefined,
  fileName: string | undefined,
): ColoredIcon {
  const fileType = fieldValues?.file_type;
  const value = fileType && "value" in fileType && typeof fileType.value === "string" ? fileType.value : undefined;
  return fileTypeIcon({ extension: value, fileName });
}

export function isPdf(fileType: string | undefined) {
  return fileType === "application/pdf";
}

export function isImage(fileType: string | undefined) {
  return ["image/jpg", "image/jpeg", "image/png", "image/gif"].includes(fileType ?? "");
}

export function isAvailableForPreview(fileType: string) {
  return isPdf(fileType) || isImage(fileType);
}

const ONLYOFFICE_SUPPORTED_EXTENSIONS = new Set([
  // documents
  "doc",
  "docm",
  "docx",
  "dot",
  "dotm",
  "dotx",
  "epub",
  "fb2",
  "fodt",
  "hml",
  "htm",
  "html",
  "hwp",
  "hwpx",
  "md",
  "mht",
  "mhtml",
  "odt",
  "ott",
  "pages",
  "rtf",
  "stw",
  "sxw",
  "txt",
  "wps",
  "wpt",
  "xml",
  // spreadsheets
  "csv",
  "et",
  "ett",
  "fods",
  "numbers",
  "ods",
  "ots",
  "sxc",
  "xls",
  "xlsb",
  "xlsm",
  "xlsx",
  "xlt",
  "xltm",
  "xltx",
  // presentations
  "dps",
  "dpt",
  "fodp",
  "key",
  "odg",
  "odp",
  "otp",
  "pot",
  "potm",
  "potx",
  "pps",
  "ppsm",
  "ppsx",
  "ppt",
  "pptm",
  "pptx",
  "sxi",
  // portable
  "djvu",
  "oxps",
  "xps",
  // diagrams
  "vsdm",
  "vsdx",
  "vssm",
  "vssx",
  "vstm",
  "vstx",
]);

export function isOnlyOfficeSupported(fileName: string | undefined): boolean {
  if (!fileName) return false;
  const ext = fileName.split(".").pop()?.toLowerCase();
  return ext ? ONLYOFFICE_SUPPORTED_EXTENSIONS.has(ext) : false;
}

export function formatMemorySize(bytes: number): string {
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  if (bytes === 0) return "0 B";

  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);

  return `${value.toFixed(1)} ${sizes[i]}`;
}

const mimeToLabel: Record<string, string> = {
  "application/pdf": "PDF",
  "application/msword": "DOC",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  "application/vnd.ms-excel": "XLS",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
  "application/vnd.oasis.opendocument.spreadsheet": "ODS",
  "application/zip": "ZIP",
  "application/x-rar-compressed": "RAR",
  "application/x-photoshop": "PSD",
  "image/vnd.adobe.photoshop": "PSD",
  "application/vnd.sketch": "SKETCH",
  "application/x-sketch": "SKETCH",
  "application/x-figma": "FIGMA",
  "image/png": "PNG",
  "image/jpeg": "JPEG",
  "image/jpg": "JPEG",
  "image/gif": "GIF",
  "text/plain": "TXT",
  "text/csv": "CSV",
  "application/json": "JSON",
  "application/xml": "XML",
  "application/vnd.ms-powerpoint": "PPT",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PPTX",
  "audio/mpeg": "MP3",
  "audio/wav": "WAV",
  "video/mp4": "MP4",
  "video/x-msvideo": "AVI",
  "application/x-shockwave-flash": "SWF",
  "application/octet-stream": "BIN",
  "application/x-tar": "TAR",
  "application/gzip": "GZ",
  "application/vnd.apple.installer+xml": "PKG",
  "application/x-msdownload": "EXE",
};

export function getFileExtension(file: { extension: string; name?: string }): string {
  if (mimeToLabel[file.extension]) {
    return mimeToLabel[file.extension];
  }

  if (file.name) {
    const parts = file.name.split(".");
    if (parts.length > 1) {
      return parts[parts.length - 1].toUpperCase();
    }
  }

  return "FILE";
}
